import { Project, Site, from, is } from '@the-real-insight/tri-model';

import { userAPI } from '../UserAPI';
import { queryBuildingsForSite } from '../utilities/buildingLookup';

/**
 * Projects, their sites, and the building behind a site.
 *
 * A project holds site ids and a site *may* embed its buildings — often it does not, and then the
 * building is found from the site's address, which is how the project's own Liegenschaften tab
 * arrives at its list. Both paths live here so every CBOS page resolves a project the same way.
 */

export const idAsString = (id: any): string | undefined => {
    if (id == null) return undefined;
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && '$oid' in id) return id.$oid;
    if (typeof id === 'object' && '_id' in id) return idAsString(id._id);
    return String(id);
};

export interface ProjectSite {
    id: string;
    label: string;
    street?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    /** The raw site document — the building lookup needs its address fields. */
    site: any;
}

export const siteLabel = (site: any): string =>
    [site?.street, site?.streetNumber].filter(Boolean).join(' ') ||
    site?.name ||
    [site?.postalCode, site?.city].filter(Boolean).join(' ') ||
    idAsString(site?._id) ||
    '';

export async function loadProjects(customer: string): Promise<any[]> {
    const projects = await userAPI.query(from(Project.type).where(is('_tenant').equal(customer)));
    return (projects ?? []).sort((first: any, second: any) =>
        String(first.name ?? '').localeCompare(String(second.name ?? ''), 'de'),
    );
}

/** The sites of a project, in a form a select box can show. */
export async function loadProjectSites(project: any): Promise<ProjectSite[]> {
    const siteIds: string[] = (project?.sites ?? [])
        .map((site: any) => idAsString(site))
        .filter((id: string | undefined): id is string => !!id);

    const sites = (
        await Promise.all(
            siteIds.map((siteId) =>
                userAPI.queryOne(from(Site.type).where(is('_id').equal(siteId))).catch((error: any) => {
                    // Logged, not swallowed: a site that cannot be read is an entry missing from
                    // the select box, and silence makes that impossible to explain.
                    console.error('Could not read a project site', siteId, error);
                    return null;
                }),
            ),
        )
    ).filter(Boolean);

    return sites
        .map((site: any) => ({
            id: idAsString(site._id) ?? '',
            label: siteLabel(site),
            street: site.street,
            streetNumber: site.streetNumber,
            postalCode: site.postalCode,
            city: site.city,
            country: site.country,
            site,
        }))
        .sort((first, second) => first.label.localeCompare(second.label, 'de'));
}

/**
 * The building a site stands for. Embedded where the site carries one, otherwise resolved from the
 * address — the same resolution the project panel uses, so both places mean the same building.
 */
export async function buildingForSite(site: any): Promise<any | null> {
    const embedded = (site?.buildings ?? [])[0];
    if (embedded && typeof embedded === 'object' && (embedded._id || embedded.id)) {
        return embedded;
    }

    const found = await queryBuildingsForSite(site).catch((error: any) => {
        console.error('Could not resolve the building of a site', idAsString(site?._id), error);
        return [];
    });

    return found[0] ?? null;
}
